import type { ChartConfig, ChartLegendStatic } from '@/components/ui/chart';
import type { PrV2AiAttributionResponse } from '@/lib/pr-v2/types';
import type { ComponentProps } from 'react';
import { createElement } from 'react';

const AI_ATTRIBUTION_SEGMENT_STYLES = [
  {
    key: 'human',
    label: 'Human',
    barClassName: 'bg-zinc-700/80',
    description: 'written without AI assistance',
  },
  {
    key: 'mixed',
    label: 'Mixed',
    barClassName: 'bg-zinc-300/70',
    description: 'generated by AI and then edited by humans',
  },
  {
    key: 'ai',
    label: 'AI-generated',
    barClassName: 'bg-zinc-100',
    description: 'generated by AI without human edits',
  },
] as const;

type AiAttributionSegmentKey = (typeof AI_ATTRIBUTION_SEGMENT_STYLES)[number]['key'];

const AI_ATTRIBUTION_SEGMENT_COLORS = {
  human: 'rgb(63 63 70 / 0.8)',
  mixed: 'rgb(212 212 216 / 0.7)',
  ai: 'rgb(244 244 245)',
} satisfies Record<AiAttributionSegmentKey, string>;

export type AiAttributionSegment = {
  key: AiAttributionSegmentKey;
  label: string;
  description: string;
  rate: number;
  widthPercent: number;
  barClassName: string;
};

export function buildAiAttributionSegments(
  aiAttribution: PrV2AiAttributionResponse | undefined,
): AiAttributionSegment[] {
  if (!aiAttribution || aiAttribution.available !== true) {
    return [];
  }

  const ratesByKey = {
    human: Math.max(aiAttribution.humanPureRate ?? 0, 0),
    mixed: Math.max(aiAttribution.mixedRate ?? 0, 0),
    ai: Math.max(aiAttribution.aiPureRate ?? 0, 0),
  };
  const totalRate = ratesByKey.human + ratesByKey.mixed + ratesByKey.ai;

  return AI_ATTRIBUTION_SEGMENT_STYLES.map((segment) => ({
    key: segment.key,
    label: segment.label,
    description: segment.description,
    rate: ratesByKey[segment.key],
    widthPercent: totalRate > 0 ? (ratesByKey[segment.key] / totalRate) * 100 : 0,
    barClassName: segment.barClassName,
  }));
}

export function buildAiAttributionLegendConfig(segments: AiAttributionSegment[]): ChartConfig {
  return segments.reduce<ChartConfig>((config, segment) => {
    config[segment.key] = {
      label: createElement(
        'span',
        { className: 'inline-flex items-baseline gap-1.5' },
        createElement('span', { className: 'text-zinc-200' }, segment.label),
        createElement('span', { className: 'text-zinc-500' }, `${segment.rate}%`),
      ),
      color: AI_ATTRIBUTION_SEGMENT_COLORS[segment.key],
    };

    return config;
  }, {});
}

export function buildAiAttributionLegendPayload(
  segments: AiAttributionSegment[],
): NonNullable<ComponentProps<typeof ChartLegendStatic>['payload']> {
  return segments.map((segment) => ({
    color: AI_ATTRIBUTION_SEGMENT_COLORS[segment.key],
    dataKey: segment.key,
    type: 'square',
    value: segment.label,
  }));
}
